import { z } from 'zod';

/**
 * Taxonomie canonique normalisée des qualifications callcenter remontées
 * depuis hipto-crm-module. Le CRM résout côté lui la valeur depuis sa table
 * `qualification_mapping → hipto_sub_metric → hipto_metric` et envoie
 * directement l'enum résolu — Doorly ne refait pas le mapping. Si la
 * résolution échoue côté CRM, le champ `category` du payload est `null`
 * et l'UI tombe en fallback sur le `rawLabel` brut.
 *
 * Liste alignée sur les exemples produits (Non contacté, Indisponible,
 * Répondeur, À rappeler) + les grandes familles `hipto_metrics`/`sub_metrics`
 * du CRM (Vente, Refus argumenté, Codification système, Contact non
 * argumenté, Codification temporaire).
 */
export const QualificationCategory = {
  /** Aucun contact tenté ou pris (lead pas encore appelé). */
  not_contacted: 'not_contacted',
  /** Personne contactée mais pas encore exploitable (rappel ultérieur prévu). */
  unavailable: 'unavailable',
  /** Tombée sur répondeur / messagerie. */
  voicemail: 'voicemail',
  /** Le prospect a demandé un rappel à un horaire précis. */
  callback_requested: 'callback_requested',
  /** Refus argumenté du prospect (pas intéressé, pas envie, etc.). */
  refused: 'refused',
  /** Vente conclue côté callcenter. */
  sold: 'sold',
  /**
   * Le client est revenu sur une vente déjà validée (délai de renonciation —
   * mutuelle Senior, réunion du 05/10/2026). Le partenaire envoie le libellé
   * « Renonciation » ; le CRM le range en « Refus argumenté », d'où ce cas à
   * part pour que le vendeur le voie. Ne touche PAS au statut de la fiche :
   * la règle de délai (au-delà, la vente reste due) est encore à fixer.
   */
  renounced: 'renounced',
  /** Non vente — le prospect n'a pas signé, sans refus argumenté. */
  not_sold: 'not_sold',
  /** Hors cible (critères d'éligibilité non remplis côté callcenter). */
  out_of_target: 'out_of_target',
  /** Déjà client du partenaire — pas d'opportunité commerciale. */
  already_client: 'already_client',
  /** Injoignable permanent (faux numéro, blacklist, décédé, etc.). */
  unreachable: 'unreachable',
  /** Doublon détecté avec un autre lead. */
  duplicate: 'duplicate',
  /** Catégorie non mappée côté CRM — l'UI tombera en fallback sur rawLabel. */
  other: 'other',
} as const;

export type QualificationCategory =
  (typeof QualificationCategory)[keyof typeof QualificationCategory];

export const QualificationCategorySchema = z.nativeEnum(QualificationCategory);

/**
 * Libellé FR affiché dans la timeline UI pour chaque catégorie. Centralisé
 * ici pour pouvoir être consommé par le serveur (analytics) et le front
 * (timeline) sans duplication. Tu peux ajuster la copie sans toucher au
 * code consommateur.
 */
export const QUALIFICATION_CATEGORY_LABEL_FR: Record<QualificationCategory, string> = {
  not_contacted: 'Non contacté',
  unavailable: 'Indisponible',
  voicemail: 'Répondeur',
  callback_requested: 'À rappeler',
  refused: 'Refus',
  sold: 'Vente',
  renounced: 'Renoncé',
  not_sold: 'Non vente',
  out_of_target: 'Hors cible',
  already_client: 'Déjà client',
  unreachable: 'Injoignable',
  duplicate: 'Doublon',
  other: 'Autre',
};

/**
 * Sous-ensemble des catégories qu'un admin peut **assigner manuellement**
 * via le picker "Rendre visible au vendor" dans la timeline.
 *
 * On limite volontairement à un sous-ensemble de valeurs sémantiquement
 * claires côté commercial. Les cas non-actionnables restants (Hors cible,
 * Déjà client, Injoignable, Autre) peuvent rester l'auto-mapping CRM côté
 * admin, mais ne sont pas exposés au vendor : pas d'action attendue.
 *
 * L'ordre est l'ordre d'affichage dans le dropdown (du cas le plus
 * fréquent en cours d'appel aux issues terminales). `duplicate` est
 * placé en fin parce que c'est un cas terminal "à classer".
 */
export const VENDOR_PROMOTABLE_CATEGORIES = [
  'not_contacted',
  'unavailable',
  'voicemail',
  'callback_requested',
  'refused',
  'sold',
  'renounced',
  'not_sold',
  'duplicate',
] as const satisfies readonly QualificationCategory[];

export type VendorPromotableCategory = (typeof VENDOR_PROMOTABLE_CATEGORIES)[number];

/**
 * Sous-groupes Hipto (`meta.subMetric` d'une qualification) reformulés pour
 * les RÉSEAUX — vendeurs, managers, responsables partenaires. L'admin garde
 * le libellé brut. « Non traité » (code `NewLead` du centre d'appels : la
 * fiche attend son premier appel) se lisait comme un abandon (Cyrille,
 * 19/10/2026). Ajouter ici toute autre reformulation, à un seul endroit.
 */
export const VENDOR_SUB_METRIC_LABEL_FR: Readonly<Record<string, string>> = {
  'Non traité': 'En cours de traitement',
};

/** Sous-groupe tel qu'un réseau le lit (inchangé s'il n'est pas reformulé). */
export function vendorSubMetricLabel(subMetric: string): string;
export function vendorSubMetricLabel(subMetric: string | null): string | null;
export function vendorSubMetricLabel(subMetric: string | null): string | null {
  if (subMetric == null) return null;
  return VENDOR_SUB_METRIC_LABEL_FR[subMetric.trim()] ?? subMetric;
}

/**
 * Texte d'une qualification tel qu'un RÉSEAU le lit (historique de la fiche,
 * colonne « Dernière qualif — sous-groupe » de l'export). Priorité :
 *   1. le libellé vendeur de la règle apprise (écran Types appris) ;
 *   2. le sous-groupe Hipto, reformulé par `vendorSubMetricLabel` ;
 *   3. `null` — l'appelant retombe sur le libellé de la catégorie.
 * L'admin, lui, garde le brut : ne pas appeler ceci pour la vue admin.
 */
export function vendorQualificationText(
  vendorLabel: string | null | undefined,
  subMetric: string | null | undefined,
): string | null {
  const label = vendorLabel?.trim();
  if (label) return label;
  return subMetric ? vendorSubMetricLabel(subMetric) : null;
}
